import {
  getIresDeviceCategoryFromSerialNumber,
  IresDeviceCategory,
  IresDevice,
  MoveHyDryDevicesPayload,
} from "../models/IresDevice";
import { getPanel, observePanel } from "./PanelRepository";
import { getComBox, observeComBox } from "./ComBoxRepository";
import { ProgramChain } from "../models/ProgramChain";
import { LatestIresDeviceInfo } from "../models/LatestIresDeviceInfo";
import { observeMostRecentProgramChain } from "./ProgramChainRepository";
import { getClientId } from "./ClientRepository";
import { firestore } from "firebase";
import { filterOutUndefined } from "@/utils/Utils";
import { iresAxiosInstance } from "@/api/IresApi";
import { db } from "@/main";

export function getIresDeviceRef(serialNumber: string) {
  const iresDeviceCategory = getIresDeviceCategoryFromSerialNumber(serialNumber)
  
  if (iresDeviceCategory === IresDeviceCategory.COMBOX) {
    return `comboxes/${serialNumber}`;
  } else {
    return `panels/${serialNumber}`;
  }
}

export async function getIresDevice(
  serialNumber: string,
  cacheAllowed?: boolean
): Promise<IresDevice> {
  const iresDeviceCategory = getIresDeviceCategoryFromSerialNumber(
    serialNumber
  );

  switch (iresDeviceCategory) {
    case IresDeviceCategory.PANEL:
      return getPanel(serialNumber, cacheAllowed);
    case IresDeviceCategory.COMBOX:
      return getComBox(serialNumber, cacheAllowed);
  }
}

export function observeLatestIresDeviceInfo(
  serialNumber: string,
  onNext: (latestIresDeviceInfo: LatestIresDeviceInfo) => void
) {
  let latestIresDevice: IresDevice | undefined = undefined;
  let latestProgramChain: ProgramChain | undefined = undefined;

  const subscriptions: Array<() => void> = [];

  const emit = () => {
    const iresDevice = latestIresDevice;

    if (iresDevice) {
      onNext({
        iresDevice,
        mostRecentProgramChain: latestProgramChain,
      });
    }
  };

  const iresDeviceCategory = getIresDeviceCategoryFromSerialNumber(
    serialNumber
  );

  switch (iresDeviceCategory) {
    case IresDeviceCategory.PANEL: {
      subscriptions.push(
        observePanel(serialNumber, (panel) => {
          latestIresDevice = panel;
          emit();
        })
      );
      break;
    }
    case IresDeviceCategory.COMBOX: {
      subscriptions.push(
        observeComBox(serialNumber, (panel) => {
          latestIresDevice = panel;
          emit();
        })
      );
      break;
    }
  }

  subscriptions.push(
    observeMostRecentProgramChain(serialNumber, (programChain) => {
      latestProgramChain = programChain;
      emit();
    })
  );

  return subscriptions;
}

/**
 * Asynchronously moves HyDry devices to a new client and optionally to a new branch.
 * It returns a boolean indicating the success of the operation.
 * 
 * @param {string[]} serialNumbers - The serial numbers of the devices to be moved.
 * @param {string} newClientId - The ID of the new client where devices will be moved.
 * @param {firestore.Timestamp} enforceAssignmentDate - The timestamp when the new assignment should be enforced.
 * @param {string} [newBranchNumber] - Optional. The new branch number to which the devices will be moved.
 * @param {boolean} [isTemporaryAssignment] - Optional. Specifies if the assignment is temporary.
 * @returns {Promise<number>} A promise that resolves to number of devices moved if the devices were successfully moved (HTTP status 201), otherwise -1.
 */
export async function moveHyDryDevicesToNewClient(
  serialNumbers: string[],
  newClientId: string,
  enforceAssignmentDate: firestore.Timestamp,
  newBranchNumber?: string | null,
  isTemporaryAssignment?: boolean,
): Promise<number> {
  try {    
    // Create the payload for the PUT request by filtering out undefined values
    const payload: MoveHyDryDevicesPayload = filterOutUndefined({
      clientId: getClientId(),
      serialNumbers: serialNumbers,
      newClientId: newClientId,
      enforceAssignmentDate: enforceAssignmentDate,
      newBranchNumber: newBranchNumber,
      isTemporaryAssignment: isTemporaryAssignment,
    });

    // Send the PUT request with the payload
    const response = await iresAxiosInstance.put(
      `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/moveHyDryDevices`,
      payload,
    );

    // Check the response status and return true for success, otherwise false
    if (response.status === 201) {
      return response.data.numberOfDevicesMoved as number;
    } else {
      return -1;
    }
  }
  catch {
    return -1;
  }
}

/**
 * Updates the device branch for all devices in the `comboxes` and `panels` collections
 * where `currentAssignment.clientId` matches the current client ID and 
 * `currentAssignment.deviceBranch` matches the specified `oldBranchNumber`.
 * 
 * @param oldBranchNumber - The branch number to search for in devices' `currentAssignment.deviceBranch`.
 * @param newBranchNumber - The new branch number to set in the matched devices' `currentAssignment.deviceBranch`.
 * 
 * @returns A promise that resolves when all devices have been updated.
 */
export async function updateIresDeviceBranch(
  oldBranchNumber: string,
  newBranchNumber: string
) {
  const clientId = getClientId();
  const iresDevicesToUpdate: firestore.DocumentSnapshot[] = [];

  // Function to query a collection and collect matching documents
  const collectMatchingDocuments = async (collectionName: string) => {
    const collectionRef = db.collection(collectionName);
    const querySnapshot = await collectionRef
      .where('currentAssignment.clientId', '==', clientId)
      .where('currentAssignment.deviceBranch', '==', oldBranchNumber)
      .get();
    iresDevicesToUpdate.push(...querySnapshot.docs);
  };

  // Collect documents from both 'comboxes' and 'panels'
  await Promise.all([
    collectMatchingDocuments('comboxes'),
    collectMatchingDocuments('panels'),
  ]);

  // Split the documents into batches of 500
  const BATCH_SIZE = 500;
  const batches = [];
  for (let i = 0; i < iresDevicesToUpdate.length; i += BATCH_SIZE) {
    batches.push(iresDevicesToUpdate.slice(i, i + BATCH_SIZE));
  }

  // Update documents in batches
  for (const batchDocs of batches) {
    const batch = db.batch();
    batchDocs.forEach((doc) => {
      batch.update(doc.ref, {
        'currentAssignment.deviceBranch': newBranchNumber,
      });
    });

    await batch.commit();
  }
}
